/**
 * STABILITY-CRITICAL: retoma a limpeza depois do reload. NUNCA apaga dados: só valida sessão,
 * baixa e libera o app. Falha => mantém o marcador e oferece "tentar de novo" (jamais refaz o
 * reset). Antes de alterar, leia docs/llm/slices/sync-engine.md e alinhe com o responsável.
 */
import { clearMarker, readMarker, stepIndex, updateMarker } from './resetMarker'
import { MAX_FARMS, initialSelection, toggleFarm } from './farmSelection'
import type { FarmOption } from './farmSelection'
import type { FlowEvent } from './flowEvents'
import { contagem } from './messages'

export type ResumePhase =
  | 'waiting_network'
  | 'validating'
  | 'pulling_base'
  | 'selecting'
  | 'downloading'
  | 'done'
  | 'session_invalid'
  | 'error'
type Failable = 'validating' | 'pulling_base' | 'selecting' | 'downloading'

export type ResumeView = {
  phase: ResumePhase
  message: string | null
  farms: FarmOption[]
  selected: string[]
  notice: string | null
  failedPhase: Failable | null
  canLeave: boolean
}

export type ResumeDeps = {
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  online(): boolean
  validateSession(): Promise<'valid' | 'invalid' | 'unreachable'>
  runBasePull(): Promise<void>
  basePullSucceeded(): Promise<boolean>
  loadFarms(): Promise<FarmOption[]>
  pullFarms(ids: string[]): Promise<void>
  replayEvents(
    events: FlowEvent[],
    extra: { farmsDownloaded: number; durationMs: number | null },
  ): Promise<void>
  pauseRuntime(): void
  finishRuntime(): void
  goToLogin(): Promise<void>
  now(): number
}

const FAIL_TEXT: Record<Failable, string> = {
  validating:
    'Não foi possível confirmar sua sessão agora. Verifique a internet e tente de novo.',
  pulling_base:
    'Não foi possível baixar a lista de fazendas. Verifique a internet e tente de novo. Seus dados antigos estão no arquivo de backup.',
  selecting: 'Não foi possível ler a lista de fazendas. Tente de novo.',
  downloading:
    'O download das fazendas não terminou. Verifique a internet e tente de novo — nada será apagado de novo.',
}

/** refreshToken() lança em falha de rede e devolve { refreshed:false } em 4xx/5xx (G033: não usar api.get). */
export async function validateSessionWith(
  refresh: () => Promise<{ refreshed?: boolean } | undefined>,
  sleep: (ms: number) => Promise<void> = (ms) =>
    new Promise<void>((r) => setTimeout(r, ms)),
  attempts = 2,
): Promise<'valid' | 'invalid' | 'unreachable'> {
  for (let i = 0; i < attempts; i++) {
    try {
      const r = await refresh()
      if (r && r.refreshed) return 'valid'
    } catch {
      return 'unreachable'
    }
    if (i < attempts - 1) await sleep(2000)
  }
  return 'invalid'
}

export function createResumeController(d: ResumeDeps) {
  let view: ResumeView = {
    phase: 'validating',
    message: null,
    farms: [],
    selected: [],
    notice: null,
    failedPhase: null,
    canLeave: false,
  }
  let listeners: Array<() => void> = []
  const set = (patch: Partial<ResumeView>) => {
    view = { ...view, ...patch }
    listeners.slice().forEach((l) => l())
  }
  const fail = (phase: Failable) =>
    set({
      phase: 'error',
      failedPhase: phase,
      message: FAIL_TEXT[phase],
      canLeave: true,
    })

  async function finish() {
    const m = readMarker(d.storage)
    try {
      await d.replayEvents(m?.ownEvents ?? [], {
        farmsDownloaded: view.selected.length,
        durationMs: m && m.startedAt ? d.now() - m.startedAt : null,
      })
    } catch {
      /* telemetria nunca trava a retomada */
    }
    clearMarker(d.storage)
    d.finishRuntime()
    set({ phase: 'done', message: null, canLeave: false })
  }

  async function advance() {
    const m = readMarker(d.storage)
    if (!m) return finish()
    let step = m.step
    let current: Failable = 'validating'
    try {
      if (stepIndex(step) < stepIndex('session_ok')) {
        if (!d.online())
          return set({
            phase: 'waiting_network',
            message: 'Sem internet. Conecte-se para continuar.',
            canLeave: true,
          })
        set({
          phase: 'validating',
          message: 'Confirmando sua sessão...',
          failedPhase: null,
          canLeave: false,
        })
        const s = await d.validateSession()
        if (s === 'invalid')
          return set({
            phase: 'session_invalid',
            message: null,
            canLeave: false,
          })
        if (s === 'unreachable') return fail('validating')
        updateMarker({ step: 'session_ok' }, d.storage)
        step = 'session_ok'
      }
      if (stepIndex(step) < stepIndex('base_pulled')) {
        current = 'pulling_base'
        set({
          phase: 'pulling_base',
          message: 'Baixando a lista de fazendas e os dados básicos...',
          failedPhase: null,
          canLeave: false,
        })
        await d.runBasePull()
        if (!(await d.basePullSucceeded())) return fail('pulling_base')
        updateMarker({ step: 'base_pulled' }, d.storage)
      }
      current = 'selecting'
      const farms = await d.loadFarms()
      const saved = m.selectedFarmIds ?? m.savedFarmIds
      const init = initialSelection(saved, farms)
      const notices: string[] = []
      if (init.truncated > 0)
        notices.push(
          `Você tinha ${saved.length} fazendas baixadas; só ${MAX_FARMS} podem ser marcadas de uma vez.`,
        )
      if (init.missing > 0)
        notices.push(
          init.missing === 1
            ? '1 fazenda que você tinha baixado não aparece mais na sua lista.'
            : `${init.missing} fazendas que você tinha baixado não aparecem mais na sua lista.`,
        )
      set({
        phase: 'selecting',
        farms,
        selected: init.selected,
        notice: notices.join(' ') || null,
        message: null,
        failedPhase: null,
        canLeave: false,
      })
    } catch {
      fail(current)
    }
  }

  async function confirm() {
    if (
      view.phase !== 'selecting' &&
      !(view.phase === 'error' && view.failedPhase === 'downloading')
    )
      return
    const ids = view.selected
    updateMarker({ step: 'downloading', selectedFarmIds: ids }, d.storage)
    if (ids.length === 0) return finish()
    set({
      phase: 'downloading',
      message: `Baixando ${contagem(ids.length, 'fazenda', 'fazendas')}... isso pode levar alguns minutos.`,
      failedPhase: null,
      canLeave: false,
    })
    try {
      await d.pullFarms(ids)
    } catch {
      return fail('downloading')
    }
    return finish()
  }

  return {
    start: async () => {
      d.pauseRuntime()
      await advance()
    },
    retry: async () => {
      if (view.phase === 'waiting_network') return advance()
      if (view.phase !== 'error') return
      if (view.failedPhase === 'downloading') return confirm()
      return advance()
    },
    toggle(id: string) {
      if (view.phase !== 'selecting') return
      const r = toggleFarm(view.selected, id)
      set({
        selected: r.selected,
        notice: r.blocked
          ? `Limite de ${MAX_FARMS} fazendas por vez. Desmarque uma para marcar outra.`
          : null,
      })
    },
    confirm,
    goToLogin: async () => {
      clearMarker(d.storage)
      await d.goToLogin()
    },
    getView: () => view,
    subscribe: (l: () => void) => {
      listeners.push(l)
      return () => {
        listeners = listeners.filter((x) => x !== l)
      }
    },
  }
}
export type ResumeController = ReturnType<typeof createResumeController>
